import { BarChart, LineChart } from '@mantine/charts';
import { Alert, SimpleGrid, Stack, Table, Text, useComputedColorScheme } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Id } from '@feature/utils';

import {
  TravelFundingGroup,
  formatTravelNumber,
  travelDeltaOf,
  travelErasmusShare,
  travelFundingByGroup,
  travelFundingGroupList,
  travelTrendWindow,
} from '../TravelAnalyticsUtils';
import { getTravelYearlyAggregatesOptions } from '../model/TravelRegistryQuery';
import { TravelAggregates } from '../model/TravelRegistryTypes';
import { TravelChartPanel } from './TravelChartPanel';
import { TravelStatTile } from './TravelStatTile';

interface TravelYearlyPanelProps {
  year: number;
  facultyId: Id | null;
}

interface YearTableProps {
  columns: string[];
  rows: { year: string; values: string[] }[];
}

const CHART_HEIGHT = 220;
const COLUMN_PROPS = { radius: [4, 4, 0, 0] as [number, number, number, number], maxBarSize: 24 };
const STACK_PROPS = { maxBarSize: 24 };
// Validated categorical slots 1-3 for each surface; "Other" is the neutral fold, not a slot.
const FUNDING_GROUP_COLORS: Record<'light' | 'dark', Record<TravelFundingGroup, string>> = {
  light: { UMFST: '#2a78d6', PROJECTS: '#eb6834', ERASMUS: '#1baf7a', OTHER: 'gray.5' },
  dark: { UMFST: '#3987e5', PROJECTS: '#d95926', ERASMUS: '#199e70', OTHER: 'gray.6' },
};

function YearTable({ columns, rows }: YearTableProps) {
  const { t } = useTranslation();

  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t('travel.registry.aggregates.yearly.year')}</Table.Th>
          {columns.map(column => (
            <Table.Th key={column}>{column}</Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map(row => (
          <Table.Tr key={row.year}>
            <Table.Td>{row.year}</Table.Td>
            {row.values.map((value, index) => (
              <Table.Td key={columns[index]}>{value}</Table.Td>
            ))}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function formatPercent(value: number) {
  return `${formatTravelNumber(value)}%`;
}

export function TravelYearlyPanel({ year, facultyId }: TravelYearlyPanelProps) {
  const { t } = useTranslation();
  const scheme = useComputedColorScheme('light');
  const { data, isError, isPending } = useQuery(
    getTravelYearlyAggregatesOptions({ ...travelTrendWindow(year), facultyId }),
  );

  if (isError) {
    return <Alert color="red">{t('travel.registry.aggregates.yearly.error')}</Alert>;
  }
  if (isPending || data == null) {
    return (
      <Text
        size="sm"
        c="dimmed"
      >
        {t('travel.registry.aggregates.yearly.loading')}
      </Text>
    );
  }

  const rows = data;
  const current = rows.find(row => row.registrationYear === year);
  const previous = rows.find(row => row.registrationYear === year - 1);
  const deltaCaption = t('travel.registry.aggregates.yearly.vsPrevious', { year: year - 1 });
  const yearOf = (row: TravelAggregates) => String(row.registrationYear);
  // A chart cannot plot the dash the panel shows for a no-travel year, so null becomes a zero column here.
  const financedEur = (row: TravelAggregates) => row.universityFinancedEur ?? 0;

  const tiles: { key: string; label: string; pick: (row: TravelAggregates) => number }[] = [
    {
      key: 'professorsTraveled',
      label: t('travel.registry.aggregates.professorsTraveled'),
      pick: row => row.professorsTraveled,
    },
    { key: 'trips', label: t('travel.registry.aggregates.trips'), pick: row => row.trips },
    { key: 'financedDays', label: t('travel.registry.aggregates.financedDays'), pick: row => row.financedDays },
    {
      key: 'financed',
      label: t('travel.registry.aggregates.yearly.universityFinancedIn'),
      pick: financedEur,
    },
  ];

  const countChart = (name: string, label: string, pick: (row: TravelAggregates) => number) => (
    <BarChart
      h={CHART_HEIGHT}
      data={rows.map(row => ({ year: yearOf(row), [name]: pick(row) }))}
      dataKey="year"
      series={[{ name, label, color: 'primary.6' }]}
      barProps={COLUMN_PROPS}
      valueFormatter={formatTravelNumber}
    />
  );
  const countTable = (label: string, pick: (row: TravelAggregates) => number) => (
    <YearTable
      columns={[label]}
      rows={rows.map(row => ({ year: yearOf(row), values: [formatTravelNumber(pick(row))] }))}
    />
  );

  const fundingGroupLabel = (group: TravelFundingGroup) => t(`travel.registry.aggregates.yearly.fundingGroup.${group}`);
  const shareLabel = t('travel.registry.aggregates.yearly.share');

  return (
    <Stack>
      <SimpleGrid cols={{ base: 2, sm: 3, lg: tiles.length }}>
        {tiles.map(tile => (
          <TravelStatTile
            key={tile.key}
            label={tile.label}
            value={current ? formatTravelNumber(tile.pick(current)) : '-'}
            delta={current ? travelDeltaOf(tile.pick(current), previous ? tile.pick(previous) : undefined) : null}
            deltaCaption={deltaCaption}
            trend={rows.map(tile.pick)}
          />
        ))}
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <TravelChartPanel
          title={t('travel.registry.aggregates.yearly.tripsTitle')}
          caption={t('travel.registry.aggregates.yearly.tripsCaption')}
          chart={countChart('trips', t('travel.registry.aggregates.trips'), row => row.trips)}
          table={countTable(t('travel.registry.aggregates.trips'), row => row.trips)}
        />
        <TravelChartPanel
          title={t('travel.registry.aggregates.yearly.financedDaysTitle')}
          caption={t('travel.registry.aggregates.yearly.financedDaysCaption')}
          chart={countChart('financedDays', t('travel.registry.aggregates.financedDays'), row => row.financedDays)}
          table={countTable(t('travel.registry.aggregates.financedDays'), row => row.financedDays)}
        />
        <TravelChartPanel
          title={t('travel.registry.aggregates.yearly.financingTitle')}
          caption={t('travel.registry.aggregates.yearly.financingCaption')}
          chart={countChart('financed', t('travel.registry.aggregates.universityFinanced'), financedEur)}
          table={countTable(t('travel.registry.aggregates.universityFinanced'), financedEur)}
        />
        <TravelChartPanel
          title={t('travel.registry.aggregates.yearly.sourcesTitle')}
          caption={t('travel.registry.aggregates.yearly.sourcesCaption')}
          chart={
            <BarChart
              h={rows.length * 36 + 40}
              data={rows.map(row => ({ year: yearOf(row), ...travelFundingByGroup(row) }))}
              dataKey="year"
              orientation="vertical"
              type="stacked"
              series={travelFundingGroupList.map(group => ({
                name: group,
                label: fundingGroupLabel(group),
                color: FUNDING_GROUP_COLORS[scheme][group],
              }))}
              barProps={STACK_PROPS}
              withLegend
              legendProps={{ verticalAlign: 'bottom' }}
              yAxisProps={{ width: 48 }}
              valueFormatter={formatTravelNumber}
            />
          }
          table={
            <YearTable
              columns={travelFundingGroupList.map(fundingGroupLabel)}
              rows={rows.map(row => {
                const totals = travelFundingByGroup(row);
                return {
                  year: yearOf(row),
                  values: travelFundingGroupList.map(group => formatTravelNumber(totals[group])),
                };
              })}
            />
          }
        />
        <TravelChartPanel
          title={t('travel.registry.aggregates.yearly.erasmusTitle')}
          caption={t('travel.registry.aggregates.yearly.erasmusCaption')}
          chart={
            <LineChart
              h={CHART_HEIGHT}
              data={rows.map(row => ({ year: yearOf(row), share: travelErasmusShare(row) }))}
              dataKey="year"
              series={[{ name: 'share', label: shareLabel, color: 'primary.6' }]}
              curveType="linear"
              strokeWidth={2}
              connectNulls
              yAxisProps={{ domain: [0, 100] }}
              valueFormatter={formatPercent}
            />
          }
          table={
            <YearTable
              columns={[shareLabel]}
              rows={rows.map(row => {
                const share = travelErasmusShare(row);
                return { year: yearOf(row), values: [share == null ? '-' : formatPercent(share)] };
              })}
            />
          }
        />
      </SimpleGrid>
    </Stack>
  );
}
